'use client'

import React, { useRef } from 'react'
import { cn } from '@/utilities/ui'
import { useDragScroll, useScrollProgress, Scrollbar } from '@/components/ui/horizontal-scroll'

interface ScrollableTableProps {
  children: React.ReactNode
  className?: string
  /** Whether to show the scroll indicator */
  showScrollbar?: boolean
}

/**
 * Horizontal scroll wrapper for tables
 * Provides drag-to-scroll and progress indicator on mobile
 */
export const ScrollableTable: React.FC<ScrollableTableProps> = ({
  children,
  className,
  showScrollbar = true,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Use horizontal-scroll hooks for drag-to-scroll and progress tracking
  useDragScroll(scrollContainerRef)
  const { scrollProgress, isVisible: showScrollIndicator } = useScrollProgress(scrollContainerRef)

  return (
    <>
      <div
        ref={scrollContainerRef}
        className={cn('w-full overflow-x-auto scrollbar-hide', className)}
        role="region"
        aria-label="Scrollable table"
        tabIndex={0}
      >
        {children}
      </div>
      {showScrollbar && (
        <Scrollbar scrollProgress={scrollProgress} isVisible={showScrollIndicator} className="mt-4" />
      )}
    </>
  )
}
